/**
 * 360-AUDIT wave 2 (E) — ONE tiny shared error surface for the small writes
 * that had none: log a call, confirm/dismiss a signal, lead-source select,
 * archive a file… Before this, those failures were silent — the office
 * believed a note was logged or a file was removed when it wasn't.
 *
 * Zero-ceremony by design: no provider, no context. `toastError("msg")` from
 * anywhere; the single <MutationToaster /> mounted in the shell area renders
 * it as a bottom-center rose banner for a few seconds. Big writes keep their
 * inline error states (banners beat toasts for anything with consequences) —
 * this is only the safety net for the one-click stuff.
 */
import { useEffect, useState } from "react";
import { AlertTriangle, X } from "lucide-react";

type Listener = (msg: string) => void;
// MUTATION-TOAST-01 (review 2026-11-02 P1 #11): one toaster now lives at the app root (main.tsx)
// and a global MutationCache onError feeds it, so any write with no handler of its own still
// tells the person it failed. Several toasters may be mounted at once; every one hears each message.
const listeners = new Set<Listener>();
const queue: string[] = [];

/** Show a transient error banner. Safe to call before the toaster mounts. */
export function toastError(msg: string) {
  if (listeners.size > 0) listeners.forEach((l) => l(msg));
  else queue.push(msg);
}

/** Convenience react-query `onError` handler: `onError: onErrToast("Couldn't save the note")`. */
export function onErrToast(prefix: string) {
  return (e: unknown) => {
    const detail = (e as Error)?.message;
    toastError(detail ? `${prefix} — ${detail}` : `${prefix} — try again.`);
  };
}

export function MutationToaster() {
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    const l: Listener = (m) => setMsg(m);
    listeners.add(l);
    if (queue.length > 0) setMsg(queue[queue.length - 1]);
    queue.length = 0;
    return () => {
      listeners.delete(l);
    };
  }, []);
  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(() => setMsg(null), 7000);
    return () => clearTimeout(t);
  }, [msg]);
  if (!msg) return null;
  return (
    <div className="fixed bottom-5 left-1/2 z-[70] flex max-w-[92vw] -translate-x-1/2 items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-800 shadow-lg">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
      <span className="min-w-0">{msg}</span>
      <button
        type="button"
        onClick={() => setMsg(null)}
        className="ml-1 shrink-0 rounded p-0.5 text-rose-400 transition-colors hover:bg-rose-100 hover:text-rose-700"
        title="Dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
